import { canvasToPngFile, downloadBlob, shareNative, type NativeShareResult } from "./nativeShare";
import { APP_DOWNLOAD_URL, PLAY_STORE_URL, viralShareFooter, setAppDownloadUrl } from "./shareCopy";
export { APP_DOWNLOAD_URL, setAppDownloadUrl };

export interface ShareReportInput {
  studentName: string;
  classTitle: string;
  score: number;
  grade: string;
  accuracyPct: number;
  npm: number;
  hits: number;
  wrongs: number;
  minutes: number;
  stageLabel?: string;
  perfect?: number;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawStars(ctx: CanvasRenderingContext2D, cx: number, cy: number, count: number): void {
  const n = Math.max(1, Math.min(5, count));
  for (let i = 0; i < 5; i++) {
    const x = cx + (i - 2) * 52;
    ctx.fillStyle = i < n ? "#ffd56a" : "rgba(255,213,106,0.18)";
    ctx.font = "700 42px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("★", x, cy);
  }
}

/** Render a 1080×1350 portrait share card — game trophy style. */
export function renderShareReportCard(input: ShareReportInput): HTMLCanvasElement {
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  // Deep blue void + cyan glow (matches app HUD)
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#031022");
  bg.addColorStop(0.4, "#061530");
  bg.addColorStop(1, "#0a2a58");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Radial gold spotlight behind grade
  const spot = ctx.createRadialGradient(W * 0.5, 420, 20, W * 0.5, 480, 420);
  spot.addColorStop(0, "rgba(255,213,106,0.45)");
  spot.addColorStop(0.45, "rgba(62,182,255,0.18)");
  spot.addColorStop(1, "rgba(62,182,255,0)");
  ctx.fillStyle = spot;
  ctx.fillRect(0, 0, W, 900);

  // Soft music-staff watermark lines
  ctx.strokeStyle = "rgba(110,200,255,0.08)";
  ctx.lineWidth = 2;
  for (let i = 0; i < 5; i++) {
    const y = 180 + i * 28;
    ctx.beginPath();
    ctx.moveTo(80, y);
    ctx.lineTo(W - 80, y);
    ctx.stroke();
  }

  // Outer frame
  roundRect(ctx, 36, 36, W - 72, H - 72, 28);
  ctx.strokeStyle = "rgba(255,213,106,0.55)";
  ctx.lineWidth = 3;
  ctx.stroke();
  roundRect(ctx, 52, 52, W - 104, H - 104, 22);
  ctx.strokeStyle = "rgba(62,182,255,0.35)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Brand
  ctx.fillStyle = "#8ad6ff";
  ctx.font = "700 26px Oxanium, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("ULTIMATE PIANO TUTOR", W / 2, 110);

  ctx.fillStyle = "rgba(242,248,255,0.55)";
  ctx.font = "600 20px Sora, system-ui, sans-serif";
  ctx.fillText("MISSION REPORT", W / 2, 148);

  // Player name
  const name = (input.studentName || "Piano Student").trim().slice(0, 26);
  ctx.fillStyle = "#fff6e8";
  ctx.font = "700 56px Oxanium, system-ui, sans-serif";
  ctx.fillText(name, W / 2, 230);

  // Stage / class plaque
  roundRect(ctx, 140, 260, W - 280, 110, 16);
  const plaque = ctx.createLinearGradient(140, 260, 140, 370);
  plaque.addColorStop(0, "rgba(10,42,88,0.95)");
  plaque.addColorStop(1, "rgba(3,16,34,0.95)");
  ctx.fillStyle = plaque;
  ctx.fill();
  ctx.strokeStyle = "rgba(255,213,106,0.5)";
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.fillStyle = "#ffd56a";
  ctx.font = "700 22px Oxanium, system-ui, sans-serif";
  ctx.fillText(input.stageLabel || "CLASS CLEAR", W / 2, 300);
  ctx.fillStyle = "#f2f8ff";
  ctx.font = "600 30px Sora, system-ui, sans-serif";
  ctx.fillText(input.classTitle.slice(0, 40), W / 2, 345);

  // Giant grade orb
  ctx.beginPath();
  ctx.arc(W / 2, 520, 118, 0, Math.PI * 2);
  const orb = ctx.createRadialGradient(W / 2 - 30, 490, 20, W / 2, 520, 118);
  orb.addColorStop(0, "#ffe8a0");
  orb.addColorStop(0.55, "#ffd56a");
  orb.addColorStop(1, "#c9a03a");
  ctx.fillStyle = orb;
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.55)";
  ctx.lineWidth = 4;
  ctx.stroke();

  ctx.fillStyle = "#031022";
  ctx.font = "800 120px Oxanium, system-ui, sans-serif";
  ctx.fillText(input.grade, W / 2, 560);

  // Stars from accuracy
  const stars =
    input.accuracyPct >= 97 ? 5 : input.accuracyPct >= 90 ? 4 : input.accuracyPct >= 80 ? 3 : input.accuracyPct >= 70 ? 2 : 1;
  drawStars(ctx, W / 2, 680, stars);

  // Score
  ctx.fillStyle = "#4dffb0";
  ctx.font = "800 78px Oxanium, system-ui, sans-serif";
  ctx.fillText(String(Math.round(input.score)), W / 2, 760);
  ctx.fillStyle = "rgba(242,248,255,0.6)";
  ctx.font = "700 22px Sora, system-ui, sans-serif";
  ctx.fillText("SESSION SCORE", W / 2, 798);

  // Stat tiles
  const stats: Array<[string, string]> = [
    ["ACCURACY", `${input.accuracyPct.toFixed(0)}%`],
    ["NPM", String(input.npm)],
    ["HITS", String(input.hits)],
    ["PERFECT", String(input.perfect ?? 0)],
  ];
  const tileW = 200;
  const gap = 18;
  const totalW = stats.length * tileW + (stats.length - 1) * gap;
  const startX = (W - totalW) / 2;
  stats.forEach(([label, value], i) => {
    const x = startX + i * (tileW + gap);
    roundRect(ctx, x, 840, tileW, 110, 14);
    ctx.fillStyle = "rgba(10,42,88,0.85)";
    ctx.fill();
    ctx.strokeStyle = "rgba(110,200,255,0.4)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = "rgba(138,214,255,0.75)";
    ctx.font = "700 16px Oxanium, system-ui, sans-serif";
    ctx.fillText(label, x + tileW / 2, 880);
    ctx.fillStyle = "#fff6e8";
    ctx.font = "800 40px Oxanium, system-ui, sans-serif";
    ctx.fillText(value, x + tileW / 2, 930);
  });

  // CTA banner
  roundRect(ctx, 100, 1000, W - 200, 180, 20);
  const cta = ctx.createLinearGradient(100, 1000, W - 100, 1180);
  cta.addColorStop(0, "rgba(255,213,106,0.28)");
  cta.addColorStop(1, "rgba(62,182,255,0.2)");
  ctx.fillStyle = cta;
  ctx.fill();
  ctx.strokeStyle = "rgba(255,213,106,0.75)";
  ctx.lineWidth = 2.5;
  ctx.stroke();

  ctx.fillStyle = "#ffd56a";
  ctx.font = "800 28px Oxanium, system-ui, sans-serif";
  ctx.fillText("BEAT THIS SCORE?", W / 2, 1040);
  ctx.fillStyle = "#f2f8ff";
  ctx.font = "600 22px Sora, system-ui, sans-serif";
  ctx.fillText("Learn classical piano easily — free classes & exams", W / 2, 1085);
  ctx.fillStyle = "#4dffb0";
  ctx.font = "700 20px Oxanium, system-ui, sans-serif";
  ctx.fillText("Share & Earn · invite friends · earn when they buy", W / 2, 1125);
  ctx.fillStyle = "#8ad6ff";
  ctx.font = "600 20px Oxanium, system-ui, sans-serif";
  ctx.fillText("Android: Google Play  ·  Windows: Microsoft Store", W / 2, 1170);

  ctx.fillStyle = "rgba(242,248,255,0.4)";
  ctx.font = "500 18px Sora, system-ui, sans-serif";
  ctx.fillText("Android · Windows  ·  LOTI Games and Apps", W / 2, 1260);

  return canvas;
}

export function buildReportShareText(input: ShareReportInput): string {
  const name = input.studentName.trim() || "I";
  return (
    `${name} just cleared “${input.classTitle}” on Ultimate Piano Tutor!\n` +
    `Grade ${input.grade} · Score ${Math.round(input.score)} · ${input.accuracyPct.toFixed(0)}% accuracy · ${input.minutes} min practice\n\n` +
    viralShareFooter()
  );
}

export async function shareReportCard(input: ShareReportInput): Promise<NativeShareResult> {
  const canvas = renderShareReportCard(input);
  const file = await canvasToPngFile(canvas, "upt-report-card.png");
  return shareNative({
    title: "My Piano Report Card",
    text: buildReportShareText(input),
    url: PLAY_STORE_URL,
    file,
    downloadName: "upt-report-card.png",
  });
}

/** Save the shareable report card PNG to the device (download). */
export async function downloadReportCard(input: ShareReportInput): Promise<void> {
  const canvas = renderShareReportCard(input);
  const file = await canvasToPngFile(canvas, "upt-report-card.png");
  if (!file) throw new Error("Could not create report image");
  downloadBlob(file, "upt-report-card.png");
}
